// Helper to get the current access token

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Makes an authenticated API request.
 *
 * @param {string} endpoint - The API endpoint path (e.g., '/license').
 * @param {string} accessToken - The user's JWT access token.
 * @param {object} options - Fetch options (method, body, etc.).
 * @param {number} retries - Number of retries for rate limiting (default 3).
 * @returns {Promise<any>} - The JSON response body.
 * @throws {Error} - Throws an error if the request fails or returns non-ok status.
 */
async function apiRequest(endpoint, accessToken, options = {}, retries = 3) {
  if (!accessToken) {
    throw new Error("Access token is required for API requests.");
  }

  const url = API_BASE_URL + endpoint;
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json', // Default to JSON
  };

  // If body is provided and it's an object, stringify it
  let body = options.body;
  if (body && typeof body === 'object') {
    body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      body, // Use the potentially stringified body
    });

    // Handle Rate Limiting (429) with retry
    if (response.status === 429 && retries > 0) {
      const retryAfter = parseInt(response.headers.get('Retry-After') || '60', 10);
      const delay = Math.min(retryAfter * 1000, 5000); // Wait seconds, max 5s
      console.warn(`Rate limited. Retrying after ${delay}ms ... ${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return apiRequest(endpoint, accessToken, options, retries - 1); // Retry
    }

    // Handle non-JSON responses for specific codes if needed (e.g., 204 No Content)
    if (response.status === 204) {
       return null; // Or handle as appropriate
    }


    // Attempt to parse JSON for all other responses
    const responseBody = await response.json();

    if (!response.ok) {
      // Throw an error with message from backend if available
      const errorMessage = responseBody?.error?.message || responseBody?.message || `HTTP error ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.body = responseBody; // Attach full body for potential debugging
      throw error;
    }

    return responseBody; // Return parsed JSON body on success

  } catch (error) {
    // Re-throw network errors or errors from above blocks
    console.error(`API Request Failed: ${options.method || 'GET'} ${endpoint}`, error);
    // Add more specific error handling or logging here if needed
    throw error; // Re-throw the error to be caught by the component
  }
}

// Export specific API functions
export const licenseService = {
  createLicense: (data, token) =>
    apiRequest('/license', token, { method: 'POST', body: data }),

  // Batch license creation with device binding
  batchCreateLicenses: (items, forceRebind, token) => {
    const requestBody = {
      items: items, 
      forceRebind: Boolean(forceRebind)
    };
    
    return apiRequest('/license/batch', token, { 
      method: 'POST', 
      body: requestBody
    });
  },

  listLicenses: (params, token) => {
    const queryParams = new URLSearchParams(params);
    return apiRequest(`/license?${queryParams.toString()}`, token, { method: 'GET' });
  },

  getLicenseExpiry: (serialNumber, token) =>
    apiRequest(`/license/${serialNumber}/expiry`, token, { method: 'GET' }),

  extendLicense: (serialNumber, durationMonths, token) =>
    apiRequest(`/license/${serialNumber}/extend`, token, {
      method: 'PUT',
      body: { durationMonths }
    }),

  // WP4 task 2: per-licence waveform eviction grant toggle (server-owned, default false).
  setEvictionGrant: (serialNumber, evictionGrant, token) =>
    apiRequest(`/license/${serialNumber}/eviction-grant`, token, {
      method: 'PUT',
      body: { evictionGrant },
    }),

  enableLicense: (serialNumber, token) =>
    apiRequest(`/license/${serialNumber}/enable`, token, { method: 'PUT' }),

  disableLicense: (serialNumber, token) =>
    apiRequest(`/license/${serialNumber}/disable`, token, { method: 'PUT' }),

  // Updated to use soft-delete endpoint
  deleteLicense: (serialNumber, token) =>
    apiRequest(`/license/${serialNumber}/soft-delete`, token, { method: 'PUT' }),

   // Add restore endpoint
   restoreLicense: (serialNumber, token) =>
       apiRequest(`/license/${serialNumber}/restore`, token, { method: 'PUT' }),

   // Add permanent delete endpoint
   permanentDeleteLicense: (serialNumber, token) =>
       apiRequest(`/license/${serialNumber}/permanent`, token, { method: 'DELETE' }),


  bindDevice: (serialNumber, deviceId, token) =>
    apiRequest(`/license/${serialNumber}/bind`, token, { method: 'PUT', body: { deviceId } }),

  unbindDevice: (serialNumber, token) =>
    apiRequest(`/license/${serialNumber}/unbind`, token, { method: 'PUT' }),

   // Add Registration Endpoints
   getRegistrationStatus: (serialNumber, token) =>
      apiRequest(`/license/${serialNumber}/registered`, token, { method: 'GET' }),

   generateRegistrationCode: (serialNumber, token) =>
      apiRequest(`/license/${serialNumber}/registration-code`, token, { method: 'GET' }),

   registerDevice: (serialNumber, deviceId, registrationCode, token) =>
      apiRequest(`/license/${serialNumber}/register`, token, { method: 'POST', body: { deviceId, registrationCode } }),

   unregisterDevice: (serialNumber, token) =>
      apiRequest(`/license/${serialNumber}/registration`, token, { method: 'DELETE' }),

   // Regenerate license key (for compromised QR codes)
   regenerateLicenseKey: (serialNumber, token) =>
      apiRequest(`/license/${serialNumber}/regenerate-key`, token, { method: 'POST' }),

   generateActivationSecret: (serialNumber, token) =>
      apiRequest(`/license/${serialNumber}/activation-secret`, token, { method: 'POST' }),

  // Customer aggregation (onboarding-stage counts per customer)
  listCustomers: (token) =>
    apiRequest('/customers', token, { method: 'GET' }),

  // Onboarding tracking: stamp labelPrintedAt / shippedAt
  setOnboardingStatus: (serialNumber, body, token) =>
    apiRequest(`/license/${serialNumber}/onboarding-status`, token, { method: 'PATCH', body }),
};
